import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardMedia, 
  Grid, 
  Container,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert
} from '@mui/material';
import { 
  CheckCircle as ApproveIcon, 
  Cancel as RejectIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../../services/api';

interface PendingImage {
  _id: string;
  locationId: {
    _id: string;
    name: string;
  };
  imageUrl: string;
  uploadedBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  status: string;
}

const PendingImagesManager: React.FC = () => {
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<PendingImage | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [processingAction, setProcessingAction] = useState<boolean>(false);

  const fetchPendingImages = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/pending-images');
      setPendingImages(response.data);
    } catch (err) {
      console.error('Error fetching pending images:', err);
      setError('Failed to load pending images. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingImages();
  }, []);

  const handleAction = (image: PendingImage, action: 'approve' | 'reject') => {
    setSelectedImage(image);
    setActionType(action);
    setOpenDialog(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedImage || !actionType) return;
    
    setProcessingAction(true);
    try {
      const endpoint = actionType === 'approve' 
        ? `/admin/pending-images/${selectedImage._id}/approve`
        : `/admin/pending-images/${selectedImage._id}/reject`;
      
      await api.post(endpoint);
      
      // Remove the processed image from the list
      setPendingImages(prev => prev.filter(img => img._id !== selectedImage._id));
      
      // Close the dialog
      setOpenDialog(false);
      setSelectedImage(null);
    } catch (err) {
      console.error(`Error ${actionType}ing image:`, err);
      setError(`Failed to ${actionType} the image. Please try again.`);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedImage(null);
    setActionType(null);
  };

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Pending Image Approvals
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchPendingImages}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : pendingImages.length === 0 ? (
        <Alert severity="info">No pending images to approve at this time.</Alert>
      ) : (
        <Grid container spacing={3}>
          {pendingImages.map((image) => (
            <Grid item xs={12} sm={6} md={4} key={image._id}>
              <Card variant="outlined">
                <CardMedia
                  component="img"
                  height="200"
                  image={image.imageUrl}
                  alt="Location image"
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Location: {image.locationId?.name || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Uploaded by: {image.uploadedBy?.name || 'Unknown user'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Date: {new Date(image.createdAt).toLocaleString()}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<ApproveIcon />}
                      onClick={() => handleAction(image, 'approve')}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<RejectIcon />}
                      onClick={() => handleAction(image, 'reject')}
                    >
                      Reject
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
      >
        <DialogTitle>
          {actionType === 'approve' ? 'Approve Image' : 'Reject Image'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {actionType === 'approve'
              ? 'This image will be added to the location and visible to all users.'
              : 'This image will be removed from storage and not added to the location.'}
          </DialogContentText>
          {selectedImage && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <img
                src={selectedImage.imageUrl}
                alt="Selected image"
                style={{ maxWidth: '100%', maxHeight: '200px' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={processingAction}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={actionType === 'approve' ? 'success' : 'error'}
            variant="contained"
            disabled={processingAction}
            startIcon={processingAction ? <CircularProgress size={20} /> : null}
          >
            {processingAction ? 'Processing...' : actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PendingImagesManager; 